/**
 * Comprehensive Error Handling Utility for CallerID Reputation Monitor
 *
 * This module provides:
 * - Custom error classes for different error types
 * - Error boundary helpers
 * - Async error handling wrappers
 * - User-friendly error message conversion
 * - Error recovery strategies
 * - Global error handlers
 */

import { Logger, LogCategory, logApiError } from './logger';

// ============================================================================
// Custom Error Classes
// ============================================================================

export enum ErrorCode {
  // Authentication Errors (1xxx)
  AUTH_INVALID_CREDENTIALS = 'AUTH_1001',
  AUTH_SESSION_EXPIRED = 'AUTH_1002',
  AUTH_UNAUTHORIZED = 'AUTH_1003',
  AUTH_FORBIDDEN = 'AUTH_1004',
  AUTH_OAUTH_FAILED = 'AUTH_1005',
  AUTH_TOKEN_INVALID = 'AUTH_1006',
  AUTH_USER_NOT_FOUND = 'AUTH_1007',
  AUTH_EMAIL_EXISTS = 'AUTH_1008',

  // Validation Errors (2xxx)
  VALIDATION_REQUIRED_FIELD = 'VAL_2001',
  VALIDATION_INVALID_FORMAT = 'VAL_2002',
  VALIDATION_OUT_OF_RANGE = 'VAL_2003',
  VALIDATION_INVALID_EMAIL = 'VAL_2004',
  VALIDATION_INVALID_PHONE = 'VAL_2005',
  VALIDATION_PASSWORD_WEAK = 'VAL_2006',
  VALIDATION_FILE_TOO_LARGE = 'VAL_2007',
  VALIDATION_INVALID_FILE_TYPE = 'VAL_2008',

  // API Errors (3xxx)
  API_NETWORK_ERROR = 'API_3001',
  API_TIMEOUT = 'API_3002',
  API_SERVER_ERROR = 'API_3003',
  API_NOT_FOUND = 'API_3004',
  API_RATE_LIMITED = 'API_3005',
  API_MALFORMED_RESPONSE = 'API_3006',

  // Data Errors (4xxx)
  DATA_NOT_FOUND = 'DATA_4001',
  DATA_DUPLICATE = 'DATA_4002',
  DATA_CORRUPTED = 'DATA_4003',
  DATA_PARSE_ERROR = 'DATA_4004',
  DATA_STORAGE_FULL = 'DATA_4005',

  // Payment Errors (5xxx)
  PAYMENT_CARD_DECLINED = 'PAY_5001',
  PAYMENT_INSUFFICIENT_FUNDS = 'PAY_5002',
  PAYMENT_INVALID_CARD = 'PAY_5003',
  PAYMENT_PROCESSING_ERROR = 'PAY_5004',
  PAYMENT_STRIPE_ERROR = 'PAY_5005',

  // Upload Errors (6xxx)
  UPLOAD_FILE_TOO_LARGE = 'UPL_6001',
  UPLOAD_INVALID_FORMAT = 'UPL_6002',
  UPLOAD_PARSE_ERROR = 'UPL_6003',
  UPLOAD_EMPTY_FILE = 'UPL_6004',
  UPLOAD_TOO_MANY_ROWS = 'UPL_6005',

  // System Errors (9xxx)
  SYSTEM_UNKNOWN = 'SYS_9001',
  SYSTEM_BROWSER_NOT_SUPPORTED = 'SYS_9002',
  SYSTEM_LOCAL_STORAGE_UNAVAILABLE = 'SYS_9003',
}

export interface ErrorDetails {
  code: ErrorCode;
  message: string;
  userMessage: string;
  field?: string;
  metadata?: Record<string, unknown>;
  recoverable: boolean;
  retryable: boolean;
  originalError?: Error;
}

export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly userMessage: string;
  public readonly field?: string;
  public readonly metadata?: Record<string, unknown>;
  public readonly recoverable: boolean;
  public readonly retryable: boolean;
  public readonly timestamp: Date;
  public readonly originalError?: Error;

  constructor(details: ErrorDetails) {
    super(details.message);
    this.name = 'AppError';
    this.code = details.code;
    this.userMessage = details.userMessage;
    this.field = details.field;
    this.metadata = details.metadata;
    this.recoverable = details.recoverable;
    this.retryable = details.retryable;
    this.timestamp = new Date();
    this.originalError = details.originalError;

    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }

    // Log the error
    Logger.error(
      LogCategory.SYSTEM,
      `AppError: ${this.code} - ${this.message}`,
      'ErrorHandler',
      {
        code: this.code,
        userMessage: this.userMessage,
        field: this.field,
        recoverable: this.recoverable,
        retryable: this.retryable,
        ...this.metadata,
      },
      this.originalError || this
    );
  }

  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      field: this.field,
      metadata: this.metadata,
      recoverable: this.recoverable,
      retryable: this.retryable,
      timestamp: this.timestamp.toISOString(),
    };
  }
}

// ============================================================================
// Specialized Error Classes
// ============================================================================

export class AuthError extends AppError {
  constructor(
    code: ErrorCode,
    message: string,
    userMessage?: string,
    metadata?: Record<string, unknown>,
    originalError?: Error
  ) {
    super({
      code,
      message,
      userMessage: userMessage || 'Authentication failed. Please try again.',
      metadata,
      recoverable: true,
      retryable: code !== ErrorCode.AUTH_FORBIDDEN,
      originalError,
    });
    this.name = 'AuthError';
  }
}

export class ValidationError extends AppError {
  constructor(
    field: string,
    message: string,
    userMessage?: string,
    code: ErrorCode = ErrorCode.VALIDATION_INVALID_FORMAT,
    metadata?: Record<string, unknown>
  ) {
    super({
      code,
      message,
      userMessage: userMessage || `Invalid value for ${field}`,
      field,
      metadata,
      recoverable: true,
      retryable: false,
    });
    this.name = 'ValidationError';
  }
}

export class ApiError extends AppError {
  public readonly status?: number;
  public readonly endpoint?: string;
  public readonly method?: string;

  constructor(
    code: ErrorCode,
    message: string,
    status?: number,
    endpoint?: string,
    method?: string,
    metadata?: Record<string, unknown>,
    originalError?: Error
  ) {
    super({
      code,
      message,
      userMessage: getApiUserMessage(code, status),
      metadata: { ...metadata, status, endpoint, method },
      recoverable: status !== 404 && status !== 403,
      retryable: isRetryableStatus(status),
      originalError,
    });
    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
    this.method = method;

    // Log API error
    logApiError(endpoint || 'unknown', method || 'unknown', status || null, originalError || this, metadata);
  }
}

export class PaymentError extends AppError {
  constructor(
    code: ErrorCode,
    message: string,
    userMessage?: string,
    metadata?: Record<string, unknown>,
    originalError?: Error
  ) {
    super({
      code,
      message,
      userMessage: userMessage || 'Payment processing failed. Please try again.',
      metadata,
      recoverable: true,
      retryable: code === ErrorCode.PAYMENT_PROCESSING_ERROR,
      originalError,
    });
    this.name = 'PaymentError';
  }
}

export class UploadError extends AppError {
  constructor(
    code: ErrorCode,
    message: string,
    userMessage?: string,
    metadata?: Record<string, unknown>
  ) {
    super({
      code,
      message,
      userMessage: userMessage || 'File upload failed. Please check your file and try again.',
      metadata,
      recoverable: true,
      retryable: false,
    });
    this.name = 'UploadError';
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function getApiUserMessage(code: ErrorCode, status?: number): string {
  switch (code) {
    case ErrorCode.API_NETWORK_ERROR:
      return 'Unable to connect to the server. Please check your internet connection.';
    case ErrorCode.API_TIMEOUT:
      return 'The request timed out. Please try again.';
    case ErrorCode.API_SERVER_ERROR:
      return 'Server error occurred. Our team has been notified.';
    case ErrorCode.API_NOT_FOUND:
      return 'The requested resource was not found.';
    case ErrorCode.API_RATE_LIMITED:
      return 'Too many requests. Please wait a moment and try again.';
    default:
      if (status && status >= 500) {
        return 'Server error occurred. Please try again later.';
      }
      return 'An error occurred while processing your request.';
  }
}

function isRetryableStatus(status?: number): boolean {
  if (!status) return true;
  // Retry on network errors (no status), server errors (5xx), or rate limiting (429)
  return status >= 500 || status === 429 || status === 0;
}

// ============================================================================
// Error Conversion Functions
// ============================================================================

export function toAppError(error: unknown): AppError {
  Logger.debug(LogCategory.SYSTEM, 'Converting error to AppError', 'ErrorHandler', {
    errorType: typeof error,
    errorName: error instanceof Error ? error.name : 'Unknown',
  });

  if (error instanceof AppError) {
    return error;
  }

  if (error instanceof Error) {
    // Check for common error patterns
    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return new ApiError(
        ErrorCode.API_NETWORK_ERROR,
        error.message,
        undefined,
        undefined,
        undefined,
        undefined,
        error
      );
    }

    if (message.includes('timeout') || message.includes('timed out')) {
      return new ApiError(
        ErrorCode.API_TIMEOUT,
        error.message,
        undefined,
        undefined,
        undefined,
        undefined,
        error
      );
    }

    if (message.includes('unauthorized') || message.includes('401')) {
      return new AuthError(
        ErrorCode.AUTH_UNAUTHORIZED,
        error.message,
        'Your session has expired. Please log in again.',
        undefined,
        error
      );
    }

    return new AppError({
      code: ErrorCode.SYSTEM_UNKNOWN,
      message: error.message,
      userMessage: 'An unexpected error occurred. Please try again.',
      recoverable: true,
      retryable: true,
      originalError: error,
    });
  }

  if (typeof error === 'string') {
    return new AppError({
      code: ErrorCode.SYSTEM_UNKNOWN,
      message: error,
      userMessage: 'An error occurred. Please try again.',
      recoverable: true,
      retryable: true,
    });
  }

  return new AppError({
    code: ErrorCode.SYSTEM_UNKNOWN,
    message: 'Unknown error occurred',
    userMessage: 'An unexpected error occurred. Please try again.',
    recoverable: true,
    retryable: true,
    metadata: { originalError: String(error) },
  });
}

// ============================================================================
// Async Error Handling Wrappers
// ============================================================================

export type AsyncResult<T> = {
  success: true;
  data: T;
} | {
  success: false;
  error: AppError;
};

export async function tryCatch<T>(
  fn: () => Promise<T>,
  context?: string
): Promise<AsyncResult<T>> {
  const timerName = `tryCatch_${context || 'unknown'}_${Date.now()}`;
  Logger.startTimer(timerName, { context });

  try {
    Logger.debug(LogCategory.SYSTEM, `Executing async operation: ${context || 'unknown'}`, 'tryCatch');
    const data = await fn();
    Logger.endTimer(timerName, { success: true });
    return { success: true, data };
  } catch (error) {
    Logger.endTimer(timerName, { success: false });
    const appError = toAppError(error);
    Logger.error(
      LogCategory.SYSTEM,
      `Async operation failed: ${context || 'unknown'}`,
      'tryCatch',
      { context },
      appError
    );
    return { success: false, error: appError };
  }
}

export function tryCatchSync<T>(
  fn: () => T,
  context?: string
): AsyncResult<T> {
  try {
    Logger.debug(LogCategory.SYSTEM, `Executing sync operation: ${context || 'unknown'}`, 'tryCatchSync');
    const data = fn();
    return { success: true, data };
  } catch (error) {
    const appError = toAppError(error);
    Logger.error(
      LogCategory.SYSTEM,
      `Sync operation failed: ${context || 'unknown'}`,
      'tryCatchSync',
      { context },
      appError
    );
    return { success: false, error: appError };
  }
}

// ============================================================================
// Retry Logic
// ============================================================================

export interface RetryOptions {
  maxAttempts: number;
  delayMs: number;
  backoffMultiplier: number;
  maxDelayMs: number;
  shouldRetry?: (error: AppError, attempt: number) => boolean;
}

const defaultRetryOptions: RetryOptions = {
  maxAttempts: 3,
  delayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 10000,
};

export async function withRetry<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {},
  context?: string
): Promise<T> {
  const opts = { ...defaultRetryOptions, ...options };
  let lastError: AppError | null = null;
  let currentDelay = opts.delayMs;

  Logger.info(LogCategory.SYSTEM, `Starting retry operation: ${context || 'unknown'}`, 'withRetry', {
    maxAttempts: opts.maxAttempts,
    initialDelay: opts.delayMs,
  });

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      Logger.debug(LogCategory.SYSTEM, `Retry attempt ${attempt}/${opts.maxAttempts}: ${context || 'unknown'}`, 'withRetry');
      const result = await fn();
      Logger.info(LogCategory.SYSTEM, `Retry operation succeeded on attempt ${attempt}: ${context || 'unknown'}`, 'withRetry');
      return result;
    } catch (error) {
      lastError = toAppError(error);

      Logger.warn(
        LogCategory.SYSTEM,
        `Retry attempt ${attempt}/${opts.maxAttempts} failed: ${context || 'unknown'}`,
        'withRetry',
        {
          attempt,
          maxAttempts: opts.maxAttempts,
          errorCode: lastError.code,
          willRetry: attempt < opts.maxAttempts && lastError.retryable,
        },
        lastError
      );

      // Check if we should retry
      const shouldRetry = opts.shouldRetry
        ? opts.shouldRetry(lastError, attempt)
        : lastError.retryable;

      if (attempt < opts.maxAttempts && shouldRetry) {
        Logger.debug(LogCategory.SYSTEM, `Waiting ${currentDelay}ms before retry`, 'withRetry');
        await new Promise(resolve => setTimeout(resolve, currentDelay));
        currentDelay = Math.min(currentDelay * opts.backoffMultiplier, opts.maxDelayMs);
      }
    }
  }

  Logger.error(
    LogCategory.SYSTEM,
    `All retry attempts exhausted: ${context || 'unknown'}`,
    'withRetry',
    { maxAttempts: opts.maxAttempts },
    lastError
  );

  throw lastError;
}

// ============================================================================
// Global Error Handlers
// ============================================================================

export function setupGlobalErrorHandlers(): void {
  Logger.info(LogCategory.SYSTEM, 'Setting up global error handlers', 'ErrorHandler');

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = toAppError(event.reason);
    Logger.fatal(
      LogCategory.SYSTEM,
      'Unhandled Promise Rejection',
      'GlobalErrorHandler',
      {
        reason: String(event.reason),
        errorCode: error.code,
      },
      error
    );
    event.preventDefault();
  });

  // Global error handler
  window.addEventListener('error', (event: ErrorEvent) => {
    Logger.fatal(
      LogCategory.SYSTEM,
      'Global Error',
      'GlobalErrorHandler',
      {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      event.error
    );
  });

  Logger.info(LogCategory.SYSTEM, 'Global error handlers configured successfully', 'ErrorHandler');
}

// ============================================================================
// Error Display Helpers
// ============================================================================

export function getErrorDisplayInfo(error: AppError): {
  title: string;
  message: string;
  actionLabel?: string;
  actionCallback?: () => void;
  severity: 'info' | 'warning' | 'error' | 'critical';
} {
  const codePrefix = error.code.split('_')[0];

  switch (codePrefix) {
    case 'AUTH':
      return {
        title: 'Authentication Error',
        message: error.userMessage,
        actionLabel: error.code === ErrorCode.AUTH_SESSION_EXPIRED ? 'Log In' : undefined,
        severity: 'warning',
      };
    case 'VAL':
      return {
        title: 'Validation Error',
        message: error.userMessage,
        severity: 'warning',
      };
    case 'API':
      return {
        title: 'Connection Error',
        message: error.userMessage,
        actionLabel: error.retryable ? 'Retry' : undefined,
        severity: error.code === ErrorCode.API_SERVER_ERROR ? 'error' : 'warning',
      };
    case 'PAY':
      return {
        title: 'Payment Error',
        message: error.userMessage,
        severity: 'error',
      };
    case 'UPL':
      return {
        title: 'Upload Error',
        message: error.userMessage,
        severity: 'warning',
      };
    default:
      return {
        title: 'Error',
        message: error.userMessage,
        severity: 'error',
      };
  }
}

// ============================================================================
// Form Validation Error Helpers
// ============================================================================

export function createValidationErrors(
  errors: Array<{ field: string; message: string; code?: ErrorCode }>
): ValidationError[] {
  return errors.map(
    ({ field, message, code }) =>
      new ValidationError(field, message, message, code)
  );
}

export function getFieldError(errors: AppError[], field: string): string | undefined {
  const fieldError = errors.find(
    (e) => e instanceof ValidationError && e.field === field
  ) as ValidationError | undefined;
  return fieldError?.userMessage;
}

// ============================================================================
// Debug Helpers
// ============================================================================

export function debugError(error: unknown): void {
  const appError = toAppError(error);
  console.group('%c[DEBUG] Error Details', 'color: #EF4444; font-weight: bold;');
  console.log('Type:', appError.name);
  console.log('Code:', appError.code);
  console.log('Message:', appError.message);
  console.log('User Message:', appError.userMessage);
  console.log('Field:', appError.field);
  console.log('Recoverable:', appError.recoverable);
  console.log('Retryable:', appError.retryable);
  console.log('Metadata:', appError.metadata);
  console.log('Stack:', appError.stack);
  if (appError.originalError) {
    console.log('Original Error:', appError.originalError);
  }
  console.groupEnd();
}
